import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import moment, { Moment } from "moment";
import { AppDatePicker } from "component/shared/date-picker";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { Select } from "component/shared/select";
import { Table, Column } from "component/shared/table";
import { parseDateParam } from "component/shared/report-filter-params";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	getMobileActivityRows,
	getMobileActivityTotal,
	getMobileActivityLoading,
	getMobileActivityLoaded,
} from "redux/selector";
import { fetchMobileActivity } from "redux/slice/mobile-activity";
import { MobileActivityReportResource } from "api/resource/mobile-activity";
import { coldFusionReplatform } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const TITLE = "Mobile Activity";

interface TypeOption {
	type: string;
	description: string;
}

// Fixed mobile event types (EventStatTypeConstant enum, statShortName LIKE 'Mobile%'), sorted by
// statShortName; "" = all. Hardcoded client-side per spec (compile-time enum, one fewer round-trip).
const MOBILE_TYPES: TypeOption[] = [
	{ type: "", description: "--- Get All Types ---" },
	{ type: "MobileContactRequest", description: "Contact Request from the Mobile App" },
	{ type: "MobileDetailPhoneCall", description: "Phone Call (Mobile Listing Detail)" },
	{ type: "MobileDetailRequestMoreInfo", description: "Request More Info (Mobile Listing Detail)" },
];

const formatDate = (iso: string): string => (iso ? moment(iso).format("MM/DD/YYYY") : "");
const formatTime = (iso: string): string => (iso ? moment(iso).format("hh:mm A") : "");

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && user.permissions.reporting}
		title={TITLE}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.BarChart2 />
				</FeatherIcon>
				{TITLE}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/mobileActivity.cfm" />}
	/>
);

const ReportsMobileBase: React.FC<Props> = ({ user, classes = {}, location, history }) => {
	const dispatch = useDispatch();
	const rows = useSelector((state: RootState) => getMobileActivityRows(state));
	const total = useSelector((state: RootState) => getMobileActivityTotal(state));
	const loading = useSelector((state: RootState) => getMobileActivityLoading(state));
	const loaded = useSelector((state: RootState) => getMobileActivityLoaded(state));

	// Defaults match CF: start = today-60, end = today, type = all. Initialized from URL params so
	// filters persist across back-nav / refresh / share (MAX-3930).
	const [type, setType] = useState(() => new URLSearchParams(location.search).get("type") ?? "");
	const [startDate, setStartDate] = useState<Moment>(() => parseDateParam(location.search, "startDate") ?? moment().subtract(60, "days"));
	const [endDate, setEndDate] = useState<Moment>(() => parseDateParam(location.search, "endDate") ?? moment());
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: "" });

	const loadReport = React.useCallback(async () => {
		try {
			// F4: .unwrap() so an API failure rejects here and surfaces an error snackbar.
			await dispatch(
				fetchMobileActivity({
					startDate: startDate.format("YYYY-MM-DD"),
					endDate: endDate.format("YYYY-MM-DD"),
					type: type || undefined,
				}) as any
			).unwrap();
		} catch{
			setSnackbar({ open: true, message: "Failed to load report" });
		}
	}, [dispatch, startDate, endDate, type]);

	useEffect(() => {
		loadReport();
	}, [dispatch]); // eslint-disable-line

	// Reflect the applied filters in the URL so they persist across back-nav / refresh / share
	// (spec-directed URL query params in place of CF's per-client session vars).
	const handleSubmit = () => {
		const params = new URLSearchParams({
			startDate: startDate.format("YYYY-MM-DD"),
			endDate: endDate.format("YYYY-MM-DD"),
		});
		if (type) {
			params.set("type", type);
		}
		history.replace({ search: params.toString() });
		loadReport();
	};

	const columns: Column<MobileActivityReportResource>[] = [
		{
			id: "type",
			title: "Type",
			value: (row) => (
				<span data-testid={`mobile-row-${row.eventStatId}`}>{row.typeDescription}</span>
			),
		},
		{
			id: "dateTime",
			title: "Date/Time",
			value: (row) => (
				<>
					<Mui.Typography variant="body2">{formatDate(row.dateTimeStamp)}</Mui.Typography>
					<Mui.Typography variant="body2" color="textSecondary">
						{formatTime(row.dateTimeStamp)}
					</Mui.Typography>
				</>
			),
		},
		{
			id: "details",
			title: "Details",
			value: (row) =>
				row.listingNumber ? (
					<Mui.Grid container direction="column">
						<Mui.Grid item>
							<Mui.Typography variant="body2">
								<span className={classes.detailLabel}>MLS #: </span>
								{row.listingNumber}
							</Mui.Typography>
						</Mui.Grid>
						{row.listing && row.listing.detailUrl && (
							<Mui.Grid item>
								<Mui.Link
									href={row.listing.detailUrl}
									target="_blank"
									rel="noopener noreferrer"
									data-testid={`mobile-listing-${row.eventStatId}`}
								>
									{row.listing.address}
								</Mui.Link>
							</Mui.Grid>
						)}
					</Mui.Grid>
				) : (
					"N/A"
				),
		},
	];

	const renderResults = () => {
		if (loaded && rows.length === 0) {
			return (
				<Mui.Typography variant="body2" color="textSecondary">
					No results found.
				</Mui.Typography>
			);
		}
		return (
			<Mui.Grid container direction="column" spacing={2}>
				<Mui.Grid item>
					<Mui.Typography variant="h6">Mobile Activity Results ({total})</Mui.Typography>
				</Mui.Grid>
				<Mui.Grid item>
					{/* Full date-filtered set from the endpoint; shared Table pages client-side (default
						10/page). Server returns dateTimeStamp DESC — no client sort applied. */}
					<Table items={rows} columns={columns} />
				</Mui.Grid>
			</Mui.Grid>
		);
	};

	return (
		<DashboardLayout
			permitted={user && user.permissions.reporting}
			title={TITLE}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.BarChart2 />
					</FeatherIcon>
					{TITLE}
				</Mui.Typography>
			}
		>
			<FullPageCard>
				<Mui.Grid container spacing={2} alignItems="flex-end" className={classes.filterBar}>
					<Mui.Grid item xs={12} sm={4} data-testid="mobile-filter-type">
						<Select
							variant="outlined"
							label="Type"
							value={type}
							options={MOBILE_TYPES}
							valueExtractor={(option: TypeOption) => option.type}
							labelExtractor={(option: TypeOption) => option.description}
							optionRenderer={(option: TypeOption) => <Mui.ListItemText primary={option.description} />}
							onChange={(value) => setType(value == null ? "" : String(value))}
						/>
					</Mui.Grid>
					<Mui.Grid item xs={6} sm={3} data-testid="mobile-filter-start">
						<AppDatePicker
								format="MM/dd/yyyy"
								fullWidth
								label="Start Date"
								placeholder="mm/dd/yyyy"
								value={startDate.toDate()}
								onChange={(date) => setStartDate(date ? moment(date) : moment())}
								openPickerIcon={<Icons.Calendar size={20} />}
								views={["day"]}
							/>
					</Mui.Grid>
					<Mui.Grid item xs={6} sm={3} data-testid="mobile-filter-end">
						<AppDatePicker
								format="MM/dd/yyyy"
								fullWidth
								label="End Date"
								placeholder="mm/dd/yyyy"
								value={endDate.toDate()}
								onChange={(date) => setEndDate(date ? moment(date) : moment())}
								openPickerIcon={<Icons.Calendar size={20} />}
								views={["day"]}
							/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={handleSubmit}
							data-testid="mobile-filter-submit"
						>
							Submit
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>

				{loading && !loaded ? <Mui.CircularProgress /> : renderResults()}
			</FullPageCard>

			<Mui.Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			>
				<MuiAlert severity="error">
					<Mui.Typography>{snackbar.message}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const ReportsMobile = withStyles(ReportsMobileBase, styles);

const ReportsMobilePageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <ReportsMobile {...props} />;
};

export const ReportsMobilePage = withStyles(ReportsMobilePageBase, styles);
